/**
 * CycleView — масштаб «Цикл» слота План (MOBILE_NAV_ARCHITECTURE.md раздел 2).
 *
 * 4–8 недель столбиками: план призраком сзади, факт акцентом спереди —
 * видно и прогрессию, и разгрузочные недели, и где факт отстал от плана.
 * Гонка и ключевые тренировки помечены на своей неделе. Касание недели
 * опускает в масштаб «Неделя» на неё же — цикл это карта, не тупик.
 *
 * Данные приходят готовыми (buildCycleWeeks) — компонент чисто
 * презентационный и проверяется рендером с фикстурой.
 */
import { useTranslation } from "react-i18next";
import { Flag } from "@phosphor-icons/react";

import type { CycleUnit, CycleWeek } from "../../pages/planScale";

const BAR_AREA_PX = 96;

export interface CycleViewProps {
  weeks: CycleWeek[];
  /** Единица серий: TSS или минуты (fallback импортных планов без load). */
  unit?: CycleUnit;
  /** Локализованное имя фазы окна (null — окно без фазы). */
  phaseLabel?: string | null;
  onWeekTap: (mondayISO: string) => void;
}

/** Минуты цикла в подпись: 1016 → «16.9ч». TSS остаётся голым числом. */
function fmtCycleValue(v: number, unit: CycleUnit, hourSuffix: string): string {
  if (unit === "min") return `${(v / 60).toFixed(1)}${hourSuffix}`;
  return String(v);
}

export function CycleView({ weeks, unit = "tss", phaseLabel, onWeekTap }: CycleViewProps) {
  const { t } = useTranslation("plan");
  const hourSuffix = t("cycle.unit_hours", { defaultValue: "ч" });
  if (weeks.length === 0) return null;

  const max = Math.max(1, ...weeks.map((w) => Math.max(w.planned, w.actual)));
  const barPx = (v: number) => (v <= 0 ? 0 : Math.max(4, Math.round((v / max) * BAR_AREA_PX)));
  const totalPlanned = weeks.reduce((s, w) => s + w.planned, 0);
  const totalActual = weeks.reduce((s, w) => s + w.actual, 0);

  return (
    <div className="indigo-card p-4 space-y-3" data-testid="plan-cycle-view">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-fg-muted">
          {phaseLabel
            ? t("cycle.title_phase", { defaultValue: "Цикл · {{phase}}", phase: phaseLabel })
            : t("cycle.title", { defaultValue: "Цикл" })}
        </div>
        <div className="text-[10.5px] font-mono text-fg-muted" data-testid="plan-cycle-totals">
          {t("cycle.totals", {
            defaultValue: "факт {{actual}} / план {{planned}}",
            actual: fmtCycleValue(totalActual, unit, hourSuffix),
            planned: fmtCycleValue(totalPlanned, unit, hourSuffix),
          })}
        </div>
      </div>

      <div className="flex items-end gap-1.5" style={{ minHeight: BAR_AREA_PX + 34 }}>
        {weeks.map((w) => (
          <button
            key={w.mondayISO}
            type="button"
            onClick={() => onWeekTap(w.mondayISO)}
            data-testid={`plan-cycle-week-${w.mondayISO}`}
            aria-label={t("cycle.week_aria", {
              defaultValue: "Неделя с {{date}}: план {{planned}}, факт {{actual}}",
              date: w.mondayISO,
              planned: fmtCycleValue(w.planned, unit, hourSuffix),
              actual: fmtCycleValue(w.actual, unit, hourSuffix),
            })}
            className={`flex-1 min-w-0 flex flex-col items-center gap-1 rounded-md pb-1 pt-1.5 transition-colors hover:bg-bg-raised ${
              w.isCurrent ? "ring-1 ring-inset ring-accent/50" : ""
            }`}
          >
            {/* Маркеры недели: гонка важнее ключевой тренировки */}
            <span className="h-3.5 flex items-center">
              {w.hasRace ? (
                <Flag size={11} weight="fill" className="text-accent" aria-hidden />
              ) : w.hasKey ? (
                <span className="w-1.5 h-1.5 rounded-full bg-accent/70" aria-hidden />
              ) : null}
            </span>
            {/* Столбики: план призраком, факт спереди */}
            <span
              className="relative w-full max-w-7 flex items-end justify-center"
              style={{ height: BAR_AREA_PX }}
              aria-hidden
            >
              <span
                className="absolute bottom-0 w-full rounded-t-sm bg-bg-raised border border-bg-divider"
                style={{ height: barPx(w.planned) }}
              />
              <span
                className="absolute bottom-0 w-[60%] rounded-t-sm bg-accent"
                style={{ height: barPx(w.actual) }}
              />
            </span>
            <span
              className={`text-[9.5px] font-mono ${w.isCurrent ? "text-fg-primary font-bold" : "text-fg-muted"}`}
            >
              {w.mondayISO.slice(8, 10)}.{w.mondayISO.slice(5, 7)}
            </span>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 text-[10px] text-fg-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-accent inline-block" aria-hidden />
          {t("cycle.legend_actual", { defaultValue: "факт" })}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-bg-raised border border-bg-divider inline-block" aria-hidden />
          {t("cycle.legend_planned", { defaultValue: "план" })}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Flag size={10} weight="fill" className="text-accent" aria-hidden />
          {t("cycle.legend_race", { defaultValue: "гонка" })}
        </span>
      </div>
    </div>
  );
}
